/* import __COLOCATED_TEMPLATE__ from './connect-button.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type IntlService from 'ember-intl/services/intl';
import ConnectButtonDropdownItem from './connect-button-dropdown-item';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';

export interface ConnectButtonArgs {
  isDisabled?: boolean;
  selectedNumberConfigurationPhoneNumberId: string | undefined;
  selectConfig?: (phoneNumber: string | undefined) => void;
  connect: () => Promise<void>;
  isLoading: boolean;
}

interface Signature {
  Args: ConnectButtonArgs;
  Element: HTMLElement;
}

export default class ConnectButton extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare finVoiceConfigService: FinVoiceConfigService;

  get isDisabled() {
    return this.args.isDisabled || this.isLoading;
  }

  get numberConfigurations() {
    return this.finVoiceConfigService.numberConfigurations;
  }

  get isLoading() {
    return this.args.isLoading;
  }

  get phoneNumbers() {
    return this.finVoiceConfigService.phoneNumbers;
  }

  get buttonLabel() {
    if (!this.args.selectedNumberConfigurationPhoneNumberId) {
      return this.intl.t('ai-agent.voice-playground.test');
    }

    let label =
      this.phoneNumbers[this.args.selectedNumberConfigurationPhoneNumberId].name ||
      this.phoneNumbers[this.args.selectedNumberConfigurationPhoneNumberId].phoneNumber ||
      this.args.selectedNumberConfigurationPhoneNumberId;

    return this.intl.t('ai-agent.voice-playground.test-configuration', {
      label,
    });
  }

  get hasNumberConfigurations() {
    return Object.keys(this.numberConfigurations).length > 0;
  }

  get shouldShowDropdown() {
    return (this.hasNumberConfigurations || this.isLoading) && !!this.args.selectConfig;
  }

  get phoneNumberOptions() {
    let defaultItems = [
      {
        text: this.intl.t('ai-agent.voice-playground.customized-configuration'),
        icon: 'play',
        isSelected: !this.args.selectedNumberConfigurationPhoneNumberId,
        onSelectItem: () => {
          this.args.selectConfig?.(undefined);
        },
      },
    ];

    let phoneNumberItems = Object.entries(this.numberConfigurations).map(([phoneNumberId, _]) => ({
      text: this.phoneNumbers[phoneNumberId].name,
      value: this.phoneNumbers[phoneNumberId].phoneNumber,
      isSelected: this.args.selectedNumberConfigurationPhoneNumberId === phoneNumberId,
      component: ConnectButtonDropdownItem,
      onSelectItem: () => {
        this.args.selectConfig?.(phoneNumberId);
      },
    }));

    let groups: {
      groupTitle: string;
      items: any[];
    }[] = [
      {
        groupTitle: this.intl.t('ai-agent.voice-playground.number-configurations'),
        items: defaultItems,
      },
    ];

    if (phoneNumberItems.length) {
      groups.push({
        groupTitle: this.intl.t('ai-agent.voice-playground.phone-numbers'),
        items: phoneNumberItems,
      });
    }

    return groups;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::VoicePlayground::ConnectButton': typeof ConnectButton;
  }
}
